import api from "../service/api";
import { processGlosaWithSimilarity } from './embeddingService'
import { ConversationMessage } from "./openaiService";

/**
 * Serviço integrado que combina tradução GPT com busca de similaridade
 */

export interface DynamicCommunicationResult {
    // Resposta em português simplificado
    resposta_pt: string

    // Array da GLOSA (estrutura LIBRAS)
    glosa: string[]

    // Animações processadas com busca de similaridade
    animacoes: {
        palavra: string
        animacao: string
        similaridade?: number
        matchExato: boolean
        datilologia: boolean
    }[]
}

/**
 * Função principal que processa uma mensagem completa
 * Retorna tudo o que é necessário para reproduzir a resposta em LIBRAS
 */
export async function processMensagem(
    mensagem: string,
    conversationHistory: ConversationMessage[] = [],
): Promise<DynamicCommunicationResult> {
    try {
        const gptResponse = await api.post('/ai/process', {
            "prompt": mensagem,
            "history": conversationHistory,
        });
        const result: DynamicCommunicationResult = gptResponse.data

        const processed = await processGlosaWithSimilarity(result.glosa)

        const animacoes = processed.map((item) => ({
            palavra: item.word,
            animacao: item.animation,
            similaridade: item.similarity,
            matchExato: item.word === item.animation,
            datilologia: item.isDactylology || false,
        }))

        return {
            resposta_pt: result.resposta_pt,
            glosa: result.glosa,
            animacoes,
        }
    } catch (error) {
        console.error('Erro ao processar mensagem:', error)
        throw error
    }
}

/**
 * Versão simplificada que retorna apenas as animações a serem reproduzidas
 */
export async function getAnimacoes(mensagem: string): Promise<string[]> {
    const resultado = await processMensagem(mensagem)
    return resultado.animacoes.map((a) => a.animacao)
}

/**
 * Função auxiliar para verificar a qualidade da tradução
 * Retorna estatísticas sobre os matches
 */
export function analisarQualidade(resultado: DynamicCommunicationResult): {
    total: number
    matchesExatos: number
    matchesSimilares: number
    qualidadeMedia: number
} {
    const total = resultado.animacoes.length
    const matchesExatos = resultado.animacoes.filter((a) => a.matchExato).length
    const matchesSimilares = total - matchesExatos

    const similaridades = resultado.animacoes
        .filter((a) => !a.matchExato && a.similaridade !== undefined)
        .map((a) => a.similaridade!)

    const qualidadeMedia =
        similaridades.length > 0
            ? similaridades.reduce((sum, val) => sum + val, 0) /
              similaridades.length
            : 1.0

    return {
        total,
        matchesExatos,
        matchesSimilares,
        qualidadeMedia,
    }
}

/**
 * Exemplo de uso com logging detalhado
 */
export async function processarComLog(
    mensagem: string,
): Promise<DynamicCommunicationResult> {
    console.log('Mensagem recebida:', mensagem)

    const resultado = await processMensagem(mensagem)

    console.log('Resposta PT:', resultado.resposta_pt)
    console.log('GLOSA:', resultado.glosa.join(' '))
    console.log('Animações:')

    resultado.animacoes.forEach((anim, index) => {
        const status = anim.matchExato
            ? 'Exato'
            : `Similar (${((anim.similaridade || 0) * 100).toFixed(1)}%)`
        console.log(
            `  ${index + 1}. ${anim.palavra} → ${anim.animacao} [${status}]`,
        )
    })

    const qualidade = analisarQualidade(resultado)
    console.log('Qualidade:')
    console.log(`Total de palavras: ${qualidade.total}`)
    console.log(`Matches exatos: ${qualidade.matchesExatos}`)
    console.log(`Matches similares: ${qualidade.matchesSimilares}`)
    console.log(
        `Qualidade média: ${(qualidade.qualidadeMedia * 100).toFixed(1)}%`,
    )

    return resultado
}
